import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { BACKEND_BASE_URL } from "./constants";
import { API_SCOPE, AUTHORITY, msalInstance } from "./msalConfig";

let cachedAccessToken = null;

const stripTrailingSlash = (value = "") =>
  value.endsWith("/") ? value.slice(0, -1) : value;

const backendBase = stripTrailingSlash(BACKEND_BASE_URL);

const buildAbsoluteUrl = (config) => {
  const requestUrl = config.url || "";
  if (requestUrl.startsWith("http")) {
    return requestUrl;
  }

  const base = config.baseURL || "";
  if (base) {
    return `${stripTrailingSlash(base)}/${requestUrl.replace(/^\//, "")}`;
  }

  return requestUrl;
};

const isBackendRequest = (config) => {
  const target = stripTrailingSlash(buildAbsoluteUrl(config));
  return target.startsWith(backendBase);
};

const acquireToken = async (forceRefresh = false) => {
  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error("No active account. User needs to sign in.");
  }

  const result = await msalInstance.acquireTokenSilent({
    scopes: [API_SCOPE],
    authority: AUTHORITY,
    account,
    forceRefresh,
  });

  cachedAccessToken = result.accessToken;
  return cachedAccessToken;
};

axios.interceptors.request.use(
  async (config) => {
    if (!isBackendRequest(config)) {
      return config;
    }

    const token = cachedAccessToken || (await acquireToken());

    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    };
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    if (
      error.response?.status === 401 &&
      isBackendRequest(originalRequest) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const token = await acquireToken(true);
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${token}`,
        };
        return axios(originalRequest);
      } catch (tokenError) {
        if (tokenError instanceof InteractionRequiredAuthError) {
          msalInstance.loginRedirect({
            scopes: [API_SCOPE],
            authority: AUTHORITY,
          });
        }
        return Promise.reject(tokenError);
      }
    }

    if (error instanceof InteractionRequiredAuthError) {
      msalInstance.loginRedirect({
        scopes: [API_SCOPE],
        authority: AUTHORITY,
      });
    }

    return Promise.reject(error);
  }
);
